import { useMemo, useState } from 'react';
import { useNavigate } from '../routing';
import { useAuth } from '../context/AuthContext';
import { ROLE_BADGE_TONES, ROLE_LABELS } from '../utils/roleDisplay';
import { APP_VERSION } from '../version';
import Icon from './Icon';

export const ROLE_BADGE = Object.fromEntries(
  Object.keys(ROLE_LABELS).map(role => [
    role,
    { label: ROLE_LABELS[role], tone: ROLE_BADGE_TONES[role] },
  ]),
);

// Initials for the avatar circle. Pulls the first two ASCII letters from the
// email's local-part — falls back to '??' for emails that have no Latin chars
// (Hebrew local-parts, etc.) so the avatar always renders something legible.
function initialsFromEmail(email) {
  if (!email) return '??';
  const local = email.split('@')[0] || '';
  const ascii = local.replace(/[^A-Za-z]/g, '');
  const src   = ascii || local;
  return (src.slice(0, 2) || '??').toUpperCase();
}

export default function UserMenu() {
  const {
    user, logout, restaurantId, restaurants,
    realRole, isRealAdmin, viewAs, viewAsTargets = [], setViewAs,
  } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  // Real role: the Command Center link describes
  // who the user IS, and a read-only preview must not take them away.
  const isAdmin = isRealAdmin;
  // An admin spans restaurants, so a preview needs one picked first (the
  // server answers 400 otherwise). Customer roles always have one.
  const previewNeedsRestaurant = isAdmin && !restaurantId;

  // The account menu shows the current restaurant read-only. Switching lives
  // in the navbar's TenantChip (a work-context control by the brand) — this
  // menu is for the account, so we don't duplicate the switcher here.
  const currentRestaurantName = useMemo(() => {
    const found = restaurants.find(r => String(r.id) === String(restaurantId));
    return found?.name || user?.restaurant?.name || null;
  }, [restaurants, restaurantId, user]);

  const handleLogout = () => {
    setOpen(false);
    logout();
    navigate('/login', { replace: true });
  };

  if (!user) return null;

  return (
    <div className="user-menu-wrapper">
      <button
        className="user-avatar"
        aria-label="תפריט משתמש"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        {initialsFromEmail(user.email)}
      </button>

      {open && (
        <>
          <div className="menu-backdrop" onClick={() => setOpen(false)} />
          <div className="user-menu">
            <div className="user-menu-header">
              <div className="user-menu-email" title={user.email}>{user.email}</div>
              <div className="user-menu-meta">
                {/* All four roles, by name. This printed every non-admin as
                    "viewer", so a manager — who can record payments, edit
                    payment terms and see negotiated discounts — was told they
                    were read-only by the one screen that shows them their own
                    role. In Hebrew, like the rest of the UI. */}
                <span className={`badge badge-${ROLE_BADGE[realRole]?.tone || 'gray'}`}>
                  {ROLE_BADGE[realRole]?.label || realRole}
                </span>
              </div>
            </div>

            {currentRestaurantName && (
              <div className="user-menu-section">
                <div className="user-menu-section-title">מסעדה</div>
                <div className="user-menu-readonly">{currentRestaurantName}</div>
              </div>
            )}

            {viewAsTargets.length > 0 && (
              <div className="user-menu-section" role="group" aria-label="צפייה כ…">
                <div className="user-menu-section-title">צפייה כ…</div>
                {viewAsTargets.map(role => (
                  <button
                    key={role}
                    type="button"
                    className={`user-menu-view-as${viewAs === role ? ' is-active' : ''}`}
                    aria-pressed={viewAs === role}
                    disabled={previewNeedsRestaurant || viewAs === role}
                    onClick={() => { setOpen(false); setViewAs(role); }}
                  >
                    <Icon name="eye" size={15} />
                    <span>{ROLE_LABELS[role]}</span>
                  </button>
                ))}
                {viewAs && (
                  <button
                    type="button"
                    className="user-menu-view-as user-menu-view-as--exit"
                    onClick={() => { setOpen(false); setViewAs(null); }}
                  >
                    חזרה לתצוגה שלי
                  </button>
                )}
                {previewNeedsRestaurant && <div className="user-menu-hint">יש לבחור מסעדה כדי לצפות כתפקיד אחר</div>}
              </div>
            )}

            {/* Peak Command Center — the internal ops console. Admin only.
                A real <a target="_blank">, not a button with window.open, so
                middle-click, ⌘-click and "copy link address" all work; the
                external glyph makes "this opens elsewhere" honest before the
                click. It belongs in the ACCOUNT menu rather than beside the
                brand: the brand cluster carries work context (the tenant chip),
                while this is an identity-scoped capability — what an admin is
                allowed to reach. */}
            {isAdmin && (
              <a
                className="user-menu-cc"
                href="/command-center"
                target="_blank"
                rel="noopener"
                onClick={() => setOpen(false)}
              >
                <Icon name="admin" size={15} />
                <span>Command Center</span>
                <Icon name="external" size={13} className="user-menu-cc-ext" />
              </a>
            )}

            <div className="user-menu-version">v{APP_VERSION}</div>

            <button className="user-menu-logout" onClick={handleLogout}>
              יציאה
            </button>
          </div>
        </>
      )}
    </div>
  );
}
